import { Clock, FileText, Users, Download } from "lucide-react";
import { Section } from "@/layouts/Section";

const DIFFERENTIATORS = [
  {
    icon: FileText,
    title: "Ontario-aligned templates",
    desc: "Built directly from the latest editions of the Ontario Ministry of Education's Report Card formats. We stay up to date with new versions from the Ministry.",
  },
  {
    icon: Clock,
    title: "From hours to minutes",
    desc: "Teachers tell us they spend several hours per reporting period on comments alone. EasyReports brings that down to a fraction of the time.",
  },
  {
    icon: Users,
    title: "Report Folder sharing & collaboration",
    desc: "Share a Report Folder with a co-teacher or occasional teacher and give them view or edit access - no shared logins required.",
  },
  {
    icon: Download,
    title: "Print-ready PDFs, instantly",
    desc: "Generate completed report card PDFs directly in the browser. No software, no formatting, no copy-pasting. Just download and submit.",
  },
];

const STATS = [
  { value: "20+", label: "students in your homeroom class, every term" },
  { value: "40+", label: "students in other classes you teach as well" },
  { value: "~8hr", label: "avg. time teachers spend on just making comments" },
  { value: <>&lt; 1hr</>, label: "with EasyReports" },
];

export default function About() {
  return (
    <div className="about-page">

      {/* ── Intro + Differentiators ─────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[820px] mx-auto">
          <div className="max-w-[640px]">
            <div className="section-label">About EasyReports</div>
            <h1 className="section-heading mb-5">
              Designed by Teachers, for Teachers
            </h1>
            <p className="section-lead">
              EasyReports was designed by an Ontario private school teacher. He had enough of big software companies making their software tools complicated. So he decided to make it easy.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 gap-6 max-[640px]:grid-cols-1">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.title} className="bg-card border border-border rounded-[14px] p-7">
                <div className="w-10 h-10 rounded-[10px] bg-brand-700/10 text-brand-600 dark:bg-brand-400/15 dark:text-brand-300 flex items-center justify-center mb-4">
                  <d.icon size={20} />
                </div>
                <h3 className="text-[1.0625rem] font-semibold text-foreground m-0 mb-2">{d.title}</h3>
                <p className="text-[0.9rem] leading-relaxed text-muted-foreground m-0">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Mission ─────────────────────────────────────────── */}
      <Section variant="muted">
        <div className="max-w-[820px] mx-auto grid grid-cols-[1fr_340px] gap-16 items-start max-[860px]:grid-cols-1 max-[860px]:gap-12">
          <div className="flex flex-col gap-4">
            <div className="section-label">Our mission</div>
            <h2 className="section-heading mb-5">
              Let educators spend their energy on students, not paperwork.
            </h2>
            
            <p className="section-lead">
              We built this for the hard working Teachers of Ontario. Whether you have 20
              students or 30, EasyReports keeps everything organized in one place so you
              don&apos;t have to spend hours copying and pasting other teacher&apos;s comments. It&apos;s easy to use
              and instantly generates the PDF export. Teachers can simply describe details about the student in their own language, and get a student-tailored comment on the report card.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            {STATS.map((s, i) => (
              <div key={i} className="bg-card border border-border rounded-[14px] px-6 py-5">
                <div className="font-display text-[2.25rem] text-brand-700 leading-none mb-[0.3rem]">{s.value}</div>
                <div className="text-[0.8125rem] text-muted-foreground leading-[1.4]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

    </div>
  );
}
